import React, { useState, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Plus, Trash2, Lock, MapPin, Calendar } from 'lucide-react';
import { calcItemPrice, formatMoney } from './calc';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentForm = ({ bookingId, total, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');
    const { error: err } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (err) {
      setError(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
      return;
    }
    // Stripe already succeeded — reconcile with our backend.
    // Retry on transient errors (502/503/504/network) since a brief backend blip
    // shouldn't strand a customer who has already been charged.
    const delays = [500, 1000, 2000, 3500, 5500];
    let lastStatus = null;
    for (let attempt = 0; attempt < delays.length; attempt++) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking_id: bookingId }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid') {
            onSuccess();
            return;
          }
          lastStatus = data.payment_status;
        } else if (res.status < 500) {
          // Non-retryable client error
          break;
        }
      } catch {
        // Network error — retry
      }
      await new Promise((r) => setTimeout(r, delays[attempt]));
    }
    setError(
      lastStatus
        ? 'Payment is still processing. It should confirm within a minute — please refresh this page. If not, contact us with your email.'
        : 'Your payment went through, but we hit a snag confirming it. You will receive an email shortly. Contact us if you don\'t.'
    );
    setProcessing(false);
  };

  return (
    <form onSubmit={handlePay} className="payment-form" data-testid="payment-form">
      <PaymentElement />
      {error && <p className="form-error" data-testid="payment-error">{error}</p>}
      <button
        type="submit"
        className="btn btn-primary btn-pay"
        disabled={!stripe || processing}
        data-testid="pay-button"
      >
        <Lock size={16} strokeWidth={2} />
        {processing ? 'Processing...' : `Pay ${formatMoney(total)}`}
      </button>
      <p className="payment-secure-note">Secure payment powered by Stripe. You'll receive an email receipt.</p>
    </form>
  );
};

const ReviewPayStep = ({ config, cart, draft, contact, removeCartItem, addAnotherProperty, prevStep, onSuccess }) => {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const stripePromiseRef = useRef(null);

  const allItems = [...cart, draft];
  const priced = allItems.map((it) => ({ item: it, price: calcItemPrice(it, config) }));
  const total = priced.reduce((s, p) => s + (p.price?.total || 0), 0);

  const startPayment = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        customer: contact,
        items: allItems.map((it) => ({
          service_type: it.service_type,
          address: it.address,
          zip_code: it.zip_code || '',
          square_feet: it.square_feet ? Number(it.square_feet) : null,
          bedrooms: Number(it.bedrooms),
          bathrooms: Number(it.bathrooms),
          add_ons: Object.entries(it.add_ons || {}).map(([key, qty]) => ({ key, qty })),
          frequency: it.frequency,
          date: it.date,
          time_window: it.time_window,
          notes: it.notes || '',
          property_verified: !!it.property_verified,
          property_details_note: it.property_details_note || '',
        })),
      };
      const res = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Could not create booking');
      }
      const booking = await res.json();
      const intentRes = await fetch(`${BACKEND_URL}/api/payments/create-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: booking.booking_id }),
      });
      if (!intentRes.ok) throw new Error('Could not initialize payment');
      const intent = await intentRes.json();
      if (!stripePromiseRef.current) {
        stripePromiseRef.current = loadStripe(intent.publishable_key);
      }
      setPayment({
        bookingId: booking.booking_id,
        bookingNumber: booking.booking_number,
        clientSecret: intent.client_secret,
        total: booking.total,
      });
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <div className="form-step" data-testid="review-pay-step">
      <h2 className="step-title">{payment ? 'Payment' : 'Review Your Booking'}</h2>
      <p className="step-description">
        {payment ? 'Complete your secure payment to confirm the booking' : 'Double-check everything before payment'}
      </p>

      <div className="review-items" data-testid="review-items">
        {priced.map(({ item, price }, idx) => (
          <div key={idx} className="review-item" data-testid={`review-item-${idx}`}>
            <div className="review-item-head">
              <div>
                <span className="review-item-service">{price?.serviceLabel || item.service_type}</span>
                <span className="review-item-address"><MapPin size={13} /> {item.address}</span>
                <span className="review-item-date">
                  <Calendar size={13} /> {item.date} • {config.time_windows.find((w) => w.key === item.time_window)?.label}
                </span>
              </div>
              <div className="review-item-right">
                <span className="review-item-total">{formatMoney(price?.total || 0)}</span>
                {idx < cart.length && !payment && (
                  <button type="button" className="review-remove" onClick={() => removeCartItem(idx)} data-testid={`remove-item-${idx}`}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
            {price && (
              <div className="review-item-lines">
                <div className="review-line">
                  <span>{price.serviceLabel}{price.sqftLabel ? ` (${price.sqftLabel})` : ''}</span>
                  <span>{formatMoney(price.base)}</span>
                </div>
                {price.discount > 0 && (
                  <div className="review-line discount">
                    <span>Recurring discount ({Math.round(price.discountRate * 100)}%)</span>
                    <span>-{formatMoney(price.discount)}</span>
                  </div>
                )}
                {price.addonLines.map((a) => (
                  <div className="review-line" key={a.key}>
                    <span>{a.label}{a.qty > 1 ? ` × ${a.qty}` : ''}</span>
                    <span>+{formatMoney(a.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="review-total" data-testid="review-total">
        <span>Total Due Today</span>
        <span className="review-total-amount">{formatMoney(payment?.total ?? total)}</span>
      </div>

      {!payment && contact.is_property_manager && (
        <button type="button" className="add-property-btn" onClick={addAnotherProperty} data-testid="add-another-property">
          <Plus size={16} /> Add Another Property
        </button>
      )}

      {error && <p className="form-error" data-testid="booking-error">{error}</p>}

      {!payment ? (
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={prevStep} data-testid="review-step-back">
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={startPayment}
            disabled={loading}
            data-testid="proceed-to-payment"
          >
            {loading ? 'Preparing...' : 'Continue to Payment'}
          </button>
        </div>
      ) : (
        <Elements
          stripe={stripePromiseRef.current}
          options={{
            clientSecret: payment.clientSecret,
            appearance: {
              theme: 'stripe',
              variables: { colorPrimary: '#1c2733', borderRadius: '10px', fontFamily: 'inherit' },
            },
          }}
        >
          <PaymentForm
            bookingId={payment.bookingId}
            total={payment.total}
            onSuccess={() => onSuccess(payment)}
          />
        </Elements>
      )}
    </div>
  );
};

export default ReviewPayStep;
